<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 1
  },
  totalPages: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])
const store = useStore()

// How many pages visible at once
const windowSize = 7

const pages = computed(() => {
  const total = props.totalPages
  const current = props.modelValue

  if (total <= 10) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const half = Math.floor(windowSize / 2)
  let start = Math.max(1, current - half)
  let end = Math.min(total, current + half)

  // Adjust window if near edges
  if (start === 1) {
    end = windowSize
  }
  if (end === total) {
    start = total - windowSize + 1
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const goTo = (page) => {
  if (page < 1 || page > props.totalPages) return
  emit('update:modelValue', page)

  store.commit("setEmailKnowledgeBase", [])
  store.commit("currentPageSelected", page)
  store.dispatch("emailKnowledgeBaseNode")
}
</script>

<template>
  <nav class="pagination">
    <button
      class="nav-btn"
      :disabled="modelValue === 1"
      @click="goTo(modelValue - 1)"
    >
      ‹
    </button>

    <!-- First page + ellipsis -->
    <button
      v-if="totalPages > 10 && pages[0] !== 1"
      @click="goTo(1)"
      class="page-btn"
    >1</button>

    <span v-if="totalPages > 10 && pages[0] !== 1" class="ellipsis">…</span>

    <!-- Main dynamic buttons -->
    <button
      v-for="page in pages"
      :key="page"
      :class="['page-btn', { active: page === modelValue }]"
      @click="goTo(page)"
    >
      {{ page }}
    </button>

    <!-- Last page + ellipsis -->
    <span
      v-if="totalPages > 10 && pages[pages.length - 1] !== totalPages"
      class="ellipsis"
    >…</span>

    <button
      v-if="totalPages > 10 && pages[pages.length - 1] !== totalPages"
      @click="goTo(totalPages)"
      class="page-btn"
    >
      {{ totalPages }}
    </button>

    <button
      class="nav-btn"
      :disabled="modelValue === totalPages"
      @click="goTo(modelValue + 1)"
    >
      ›
    </button>
  </nav>
</template>

<style scoped>
.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: -apple-system, BlinkMacSystemFont, "San Francisco", Helvetica, Arial, sans-serif;
}

.page-btn,
.nav-btn {
  background: #ffffff;
  border: 1px solid #d1d1d6;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  min-width: 36px;
  transition: all 0.2s ease;
  color: #333;
}

.page-btn:hover,
.nav-btn:hover {
  background: #f2f2f7;
}

.page-btn.active {
  background: #007aff;
  color: white;
  border-color: #007aff;
  font-weight: 500;
}

.page-btn:disabled,
.nav-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.ellipsis {
  padding: 0 4px;
  color: #8e8e93;
}
</style>
